import { Injectable } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { Observable, of } from 'rxjs';
import { map, tap } from 'rxjs/operators';
import { GatherApiService } from './gather-api.service';
import { LocationPickerComponent } from '../../talent-happy-path/shared/components/location-picker/location-picker.component';
import { TalentMoatsTravel } from '../../../typings';

/**
 * The home location a talent can adjust while responding to a gather, shared by the Experience and the Not-a-Fit
 * pages: reads the location already on file, prefills the picker with it, and saves a changed one.
 *
 * <p>Holds the location last known to be stored, so an untouched or unchanged field never posts. That state is
 * per-page, so provide this on the component rather than in root.
 */
@Injectable()
export class GatherHomeLocationService {

  private savedHomeLocation: string = null;
  private prefilledPicker: LocationPickerComponent = null;

  constructor(private gatherApiService: GatherApiService) {
  }

  /** Reads the location on file */
  load(trackingId: string): Observable<void> {
    return this.gatherApiService.getMoatsTravel(trackingId).pipe(
      map(travel => {
        this.savedHomeLocation = travel?.homeLocation || null;
      })
    );
  }

  /** Shows the location on file in the picker */
  prefill(locationPicker: LocationPickerComponent, form: FormGroup): void {
    if (!locationPicker || locationPicker === this.prefilledPicker || !this.savedHomeLocation) {
      return;
    }

    this.prefilledPicker = locationPicker;
    const loaded = this.savedHomeLocation;
    void locationPicker.setLocationValue(loaded).then(() => {
      // The picker resolves the stored text through Google, so keep the resolved form as the saved value -
      // otherwise the prefill itself would read as a change and post on the next submit.
      this.savedHomeLocation = ((form.get('homeLocation')?.value as string) || '').trim() || loaded;
    });
  }

  /** Saves a changed home location */
  save(trackingId: string, personId: number, form: FormGroup): Observable<void> {
    const control = form.get('homeLocation');
    const homeLocation = ((control?.value as string) || '').trim();
    const geoLocation = form.get('geoLocation')?.value as GeoLocation;

    if (!homeLocation || control.invalid || !geoLocation || homeLocation === this.savedHomeLocation) {
      return of(undefined);
    }

    const travel: TalentMoatsTravel = {
      personId: personId,
      homeLocation: homeLocation,
      homeLocationLatitude: geoLocation.latitude,
      homeLocationLongitude: geoLocation.longitude,
      modifyingPersonId: personId
    };

    return this.gatherApiService.saveMoatsTravel(trackingId, travel).pipe(
      tap(() => {
        this.savedHomeLocation = homeLocation;
      })
    );
  }
}
